import React from "react";
import { FaBalanceScale, FaHandshake, FaGavel, FaShieldAlt } from "react-icons/fa";
import courtroomBg from "../../assets/images/Herosection/herobanner.jpg";

const WhyChooseUs = () => {
  const pillars = [
    {
      title: "19+ Years of Mastery",
      desc: "A legacy of transparency and results-driven advocacy in Delhi's highest courts.",
      icon: <FaBalanceScale />,
    },
    {
      title: "Client-First Ethics",
      desc: "Every case is handled with precision, strategy, and a commitment to your peace of mind.",
      icon: <FaHandshake />,
    },
    {
      title: "Strategic Litigation",
      desc: "From first consultation to final outcome, we stand by you with consistent courtroom clarity.",
      icon: <FaShieldAlt />,
    },
    {
      title: "Dedicated Advocacy",
      desc: "Specialized expertise across civil, criminal, corporate, and matrimonial legal matters.",
      icon: <FaGavel />,
    },
  ];

  return (
    <section className="relative overflow-hidden font-serif bg-white">

      {/* ── UPPER HERO ── */}
      <div className="relative py-20 sm:py-24 lg:py-36 px-4 sm:px-6">

        {/* Background: greyscale image + near-black scrim */}
        <div className="absolute inset-0 z-0">
          <img
            src={courtroomBg}
            alt="Rai & Associates Law Chambers"
            className="w-full h-full object-cover grayscale brightness-[0.15]"
          />
          {/* Fine grain texture overlay for depth */}
          <div className="absolute inset-0 bg-black/70" />
        </div>

        {/* Thin top rule */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-[2px] bg-white/30" />

        <div className="max-w-7xl mx-auto relative z-10 text-center">
          {/* Eyebrow */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <span className="block w-8 h-[1px] bg-white/40" />
            <p className="uppercase tracking-[0.5em] text-[10px] sm:text-xs font-bold text-white/60">
              Rai & Associates
            </p>
            <span className="block w-8 h-[1px] bg-white/40" />
          </div>

          {/* Headline */}
          <h2 className="text-3xl sm:text-5xl md:text-6xl text-white font-bold mb-8 tracking-tight leading-tight">
            Why Clients <br />
            <span className="font-light text-white/60">Choose Our Counsel</span>
          </h2>

          {/* Body */}
          <div className="max-w-2xl mx-auto">
            <p className="text-white/50 text-base sm:text-lg md:text-xl leading-relaxed font-sans font-light">
              Trusted for our transparency, dedication, and results. We combine
              deep legal expertise with a genuine commitment to client success,
              ensuring every case is handled with surgical precision.
            </p>
          </div>
        </div>

        {/* Thin bottom rule */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-16 h-[1px] bg-white/20" />
      </div>

      {/* ── LOWER CONTENT ── */}
      <div className="bg-white py-0 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto -mt-20 sm:-mt-28 relative z-20">

          {/* PILLAR CARDS */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 shadow-[0_8px_40px_rgba(0,0,0,0.18)] border border-black/10">
            {pillars.map((item, index) => (
              <div
                key={index}
                className="
                  bg-white p-8 sm:p-10
                  border-b sm:border-b-0 border-r-0
                  sm:border-r border-black/8
                  last:border-b-0 last:border-r-0
                  [&:nth-child(2)]:border-r-0 sm:[&:nth-child(2)]:border-r
                  lg:[&:nth-child(2)]:border-r
                  group hover:bg-black transition-all duration-500 cursor-default
                "
                style={{ borderColor: "rgba(0,0,0,0.07)" }}
              >
                {/* Icon */}
                <div className="text-2xl sm:text-3xl mb-5 text-black/30 group-hover:text-white/40 transition-colors duration-500">
                  {item.icon}
                </div>

                {/* Number accent */}
                <span className="block text-[10px] uppercase tracking-[0.4em] text-black/25 group-hover:text-white/25 mb-3 font-sans transition-colors duration-500">
                  {String(index + 1).padStart(2, "0")}
                </span>

                {/* Title */}
                <h4 className="text-base sm:text-lg font-bold text-black mb-3 group-hover:text-white transition-colors duration-500 uppercase tracking-wide">
                  {item.title}
                </h4>

                {/* Divider */}
                <div className="w-8 h-[1px] bg-black/20 group-hover:bg-white/20 mb-4 transition-colors duration-500" />

                {/* Description */}
                <p className="text-black/50 text-sm leading-relaxed font-sans group-hover:text-white/50 transition-colors duration-500">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          {/* ── FLOATING CTA ── */}
          <div className="mt-16 sm:mt-20 mb-16 sm:mb-20 max-w-4xl mx-auto relative border border-black/10 bg-black">

            {/* Corner accents */}
            <span className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-white/20" />
            <span className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-white/20" />
            <span className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-white/20" />
            <span className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-white/20" />

            <div className="p-8 sm:p-12 text-center">
              {/* Top thin line */}
              <div className="w-12 h-[1px] bg-white/20 mx-auto mb-8" />

              <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
                Got a Problem?{" "}
                <span className="font-light text-white/60">Consult With Us</span>
              </h3>

              <p className="text-white/40 font-sans text-sm sm:text-base mb-10 max-w-xl mx-auto leading-relaxed">
                Facing a legal issue? Rai & Associates provides expert guidance
                and practical solutions to protect your rights.
              </p>

              <a
                href="/contact-us"
                className="
                  inline-block px-8 sm:px-12 py-3 sm:py-4
                  text-[10px] sm:text-xs font-bold uppercase tracking-[0.35em]
                  text-black bg-white
                  border border-white
                  hover:bg-transparent hover:text-white
                  transition-all duration-400
                "
              >
                Schedule Consultation Now
              </a>

              {/* Bottom thin line */}
              <div className="w-12 h-[1px] bg-white/20 mx-auto mt-8" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;